// Applet widget: a tile whose body an applet draws (docs/applets.md). The tile's kind is `applet` and its data names
// the applet and the widget (data.applet, data.widget, reserved keys; the rest of data is the widget's own state,
// saved through its context's setData). The applet registers its widgets while it is active (registerAppletWidget,
// called by the widgets namespace of @webtop/api) with {id, name, about?, icon?, sizes, look?, drag?, render(el, ctx)
// -> cleanup?, update?(ctx), component? (React), menu?(ctx), head?(ctx)}: look 'card' (the default) puts the body
// under a card head with the tile's title, 'bare' gives the body the whole tile; drag: true makes the body drag the
// tile like a built-in widget's (a widget that only shows something, with buttons at most), where otherwise its
// pointer and key events are the applet's. head(ctx) shapes a card's head: {icon?, iconColor? (a CSS colour for the
// icon), title? (shown when the tile has none), stale?, buttons? (nodes after the title: a refresh button, quick
// choices), background? (a colour for the card, `.light` on the tile when it is light; null for the applets'
// default), hidden? (no head at all: the body is the whole card, a page filling it)}, read when the tile is drawn and
// on every update. A tile of one that is not registered (the applet is not
// installed here, is disabled, or is still loading) shows a placeholder saying so, with Install or Enable where that
// can be done (the runtime says what it knows through setAppletHost). The Add section lists the registered
// widgets (appletWidgets), never this kind itself (`hidden`). A kept tile (keep: the registration is the same) is
// updated in place through fit(), so a React widget keeps its state across redraws.
import { cardHead, currentRole, el$, isLight, lineIcon, render as redraw, saveData, sizeKey, sizeOf, patchTile, toast } from './kit.js';
import { SIZES_ALL } from '/applet-model.js';

export const RESERVED = ['applet', 'widget']; // keys of data that are the tile's address, not the widget's state
const registry = new Map(); // `${appletId}/${widgetId}` -> {applet, def, generation, instances: Map(tile id -> instance)}
let generation = 0;
let host = { need: async () => 'unknown', install: null, enable: null, nameOf: () => null };
/** The runtime's hook: need(appletId) -> 'active' | 'installing' | 'disabled' | 'available' | 'unknown' | 'off' (the
 *  server only runs what comes with webtop) | 'dev' (a developer applet, Developer Mode off), activating it where it can;
 *  install(appletId) / enable(appletId) for the placeholder's button; nameOf(appletId). */
export function setAppletHost(h) { host = { ...host, ...h }; }

export function registerAppletWidget(applet, def) {
  if (!def?.id || typeof def.id !== 'string') throw new Error('A widget needs an id');
  const key = `${applet}/${def.id}`;
  const old = registry.get(key);
  if (old) dropInstances(old);
  const entry = { applet, def: { sizes: ['2x2'], look: 'card', ...def }, generation: ++generation, instances: new Map() };
  registry.set(key, entry);
  redraw();
  return () => {
    if (registry.get(key) !== entry) return;
    dropInstances(entry);
    registry.delete(key);
    redraw();
  };
}
function dropInstances(entry) {
  for (const inst of entry.instances.values()) cleanup(inst);
  entry.instances.clear();
}
function cleanup(inst) {
  try { inst.cleanup?.(); } catch (e) { console.error(e); }
  inst.react?.unmount();
}
/** The registered widgets, for the Add section and the launcher: {applet, id, name, about, icon, sizes}. */
export const appletWidgets = () => [...registry.values()].map(({ applet, def }) => ({
  applet, id: def.id, name: def.name ?? def.id, about: def.about ?? '', icon: def.icon ?? null, sizes: def.sizes,
}));
/** The tiles showing widget `widgetId` of `applet` (their ids). */
export const widgetInstances = (applet, widgetId) => [...(registry.get(`${applet}/${widgetId}`)?.instances.keys() ?? [])];
/** Redraw every tile of a widget (its data may have changed elsewhere). */
export function refreshWidget(applet, widgetId) {
  const entry = registry.get(`${applet}/${widgetId}`);
  if (!entry) return;
  entry.generation = ++generation;
  redraw();
}
const entryOf = (s) => registry.get(`${s.data?.applet}/${s.data?.widget}`) ?? null;
const stateOf = (s) => Object.fromEntries(Object.entries(s.data ?? {}).filter(([k]) => !RESERVED.includes(k)));

export default {
  kind: 'applet',
  label: 'Applet Widget',
  icon: lineIcon('applets'),
  about: 'A widget provided by an applet',
  look: 'card',
  hidden: true, // the Add section lists the registered widgets instead
  sizes: SIZES_ALL,
  sizesOf: (s) => entryOf(s)?.def.sizes ?? SIZES_ALL,
  labelOf: (s) => entryOf(s)?.def.name ?? host.nameOf(s.data?.applet) ?? 'Applet Widget', // its menu's heading
  create: () => ({ data: {} }),
  render: renderApplet,
  fit: (s, el) => el.widgetUpdate?.(s),
  keep: (s) => `${s.data?.applet}/${s.data?.widget}/${entryOf(s)?.generation ?? 'none'}`,
  fields: '.applet-body:not(.drag)',
  menu: (s) => {
    const entry = entryOf(s);
    const inst = entry?.instances.get(s.id);
    const items = inst && typeof entry.def.menu === 'function' ? entry.def.menu(inst.ctx) : [];
    return Array.isArray(items) ? items.filter((it) => it && typeof it === 'object' && typeof it.label === 'string') : [];
  },
};

function renderApplet(s, el) {
  const entry = entryOf(s);
  el.classList.toggle('bare', entry?.def.look === 'bare');
  el.classList.toggle('card', entry?.def.look !== 'bare');
  const paint = (color) => {
    el.style.setProperty('--card-bg', color || 'var(--applet-card)');
    el.classList.toggle('light', !!color && isLight(color));
  };
  paint(null);
  const name = entry?.def.name ?? host.nameOf(s.data?.applet) ?? s.data?.widget ?? 'Applet';
  let head = cardHead({ icon: entry?.def.icon ? entry.def.icon : lineIcon('applets'), title: s.title || name });
  const body = el$('div', entry?.def.drag ? 'applet-body drag' : 'applet-body');
  const bare = entry?.def.look === 'bare';
  el.replaceChildren(...(bare ? [] : [head]), body);
  el.widgetUpdate = null;
  if (!entry) return placeholder(s, body, head);
  const old = entry.instances.get(s.id);
  if (old) cleanup(old);
  const inst = { ctx: null, cleanup: null, react: null };
  entry.instances.set(s.id, inst);
  // The head as the widget shapes it (head(ctx)), drawn again on every update; the default head stays otherwise.
  const shapeHead = () => {
    if (bare || typeof entry.def.head !== 'function') return;
    let spec = {};
    try { spec = entry.def.head(inst.ctx) ?? {}; } catch (e) { console.error(e); }
    paint(typeof spec.background === 'string' ? spec.background : null);
    el.style.setProperty('--card-icon', typeof spec.iconColor === 'string' ? spec.iconColor : '');
    const next = cardHead({ icon: spec.icon ?? (entry.def.icon ? entry.def.icon : lineIcon('applets')), title: s.title || spec.title || name, stale: !!spec.stale }, ...(Array.isArray(spec.buttons) ? spec.buttons : []));
    if (spec.hidden) next.style.display = 'none'; // inline: kit.css gives .card-head its display
    head.replaceWith(next);
    head = next;
  };
  const ctx = () => ({
    tile: { id: s.id, title: s.title, size: sizeKey(s) },
    data: stateOf(s),
    size: sizeOf(s),
    editor: currentRole() === 'editor',
    setData: async (patch) => {
      const clean = Object.fromEntries(Object.entries(patch ?? {}).filter(([k]) => !RESERVED.includes(k)));
      await saveData(s, clean);
      el.widgetUpdate?.(s);
    },
    setTitle: async (title) => {
      try { Object.assign(s, await patchTile(s, { title: String(title).slice(0, 80) })); } catch (e) { toast(e); }
      const t = head.querySelector('.card-title');
      if (t) t.textContent = s.title || name;
    },
  });
  inst.ctx = ctx();
  shapeHead();
  if (entry.def.component) {
    import('@webtop/components').then(({ renderReact }) => {
      if (entry.instances.get(s.id) !== inst) return; // redrawn meanwhile
      inst.react = renderReact(body, entry.def.component, inst.ctx, { widget: true });
    }).catch((e) => { body.replaceChildren(note(`Could not load React: ${e.message}`)); });
  } else if (typeof entry.def.render === 'function') {
    try { inst.cleanup = entry.def.render(body, inst.ctx) ?? null; } catch (e) { console.error(e); body.replaceChildren(note(e.message)); }
  }
  el.widgetUpdate = (tile) => {
    s = tile;
    inst.ctx = ctx();
    shapeHead();
    if (inst.react) inst.react.update(inst.ctx);
    else if (typeof entry.def.update === 'function') { try { entry.def.update(inst.ctx, body); } catch (e) { console.error(e); } }
  };
}
const note = (text) => el$('p', 'widget-note', text);
function placeholder(s, body, head) {
  const id = s.data?.applet;
  let name = host.nameOf(id) ?? id ?? 'an applet';
  const line = note(`Needs the applet “${name}”…`);
  body.append(line);
  // 'installing' is the runtime mid-change (the tile may be kept through its redraws): ask again until it settles
  const settled = async () => {
    for (let i = 0; i < 80; i++) {
      const state = await host.need(id);
      if (state !== 'installing' || !body.isConnected) return state;
      await new Promise((r) => setTimeout(r, 250));
    }
    return 'unknown';
  };
  settled().then((state) => {
    if (!body.isConnected) return;
    if (state === 'active') return redraw();
    // the server's list has been read by now (need() asks it), so the applet's name is known where it was not
    name = host.nameOf(id) ?? name;
    if (!s.title) head.querySelector('.card-title').textContent = name;
    const text = { installing: 'Installing…', disabled: `The applet “${name}” is disabled`, available: `Needs the applet “${name}”`, unknown: `Needs the applet “${name}”, which is not installed`, off: 'This server only runs the applets that come with webtop', dev: `“${name}” is a developer applet: turn on Developer Mode in Settings › Applets` }[state] ?? '';
    line.textContent = text;
    const act = state === 'disabled' ? ['Enable', () => host.enable?.(id)] : state === 'available' ? ['Install', () => host.install?.(id)] : null;
    if (!act) return;
    const b = el$('button', 'applet-fix', act[0]);
    b.type = 'button';
    b.addEventListener('click', async () => { b.disabled = true; try { await act[1](); } catch (e) { toast(e); b.disabled = false; } });
    body.append(b);
  });
}
